/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\DeviceExpansionTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import { AlertCircle, RefreshCw, Edit, Keyboard } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import Combobox from '../../../components/ui/Combobox';
import { useI18n } from '../../../hooks/useI18n';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { extractExpansionModuleInfo } from '../../provisioning-templates/utils/extractOptions';
import { ExpansionKeyConfig } from '../../provisioning-templates/types';
import type { DeviceModel } from '../../provisioning-device-models/api';
import type { Template } from '../../provisioning-templates/api';

interface DeviceExpansionTabProps {
  selectedModel: DeviceModel;
  selectedTemplate: Template | null;
  deviceExpansionModules: ExpansionKeyConfig[];
  setDeviceExpansionModules: React.Dispatch<React.SetStateAction<ExpansionKeyConfig[]>>;
  isExpansionModulesOverridden: boolean;
  setIsExpansionModulesOverridden: (val: boolean) => void;
  expPage: number;
  setExpPage: React.Dispatch<React.SetStateAction<number>>;
  expPerPage: number;
  setExpPerPage: React.Dispatch<React.SetStateAction<number>>;
  selectedExpModule: number;
  setSelectedExpModule: React.Dispatch<React.SetStateAction<number>>;
}

export function DeviceExpansionTab({
  selectedModel, selectedTemplate, deviceExpansionModules, setDeviceExpansionModules,
  isExpansionModulesOverridden, setIsExpansionModulesOverridden,
  expPage, setExpPage, expPerPage, setExpPerPage,
  selectedExpModule, setSelectedExpModule
}: DeviceExpansionTabProps) {
  const { t } = useI18n();
  const expInfo = extractExpansionModuleInfo(selectedModel.expansionModuleKeys, selectedModel.accounts);
  const moduleKeys = deviceExpansionModules.filter((k: { moduleNumber?: number }) => k.moduleNumber === selectedExpModule);
  const totalItems = moduleKeys.length;
  const totalPages = Math.ceil(totalItems / expPerPage);
  const startIndex = (expPage - 1) * expPerPage;
  const endIndex = Math.min(startIndex + expPerPage, totalItems);
  const currentPageKeys = moduleKeys.slice(startIndex, endIndex);
  const lineOptions = Array.from({ length: expInfo.maxAccounts }, (_, i) => ({ value: `Line ${i + 1}`, label: `Line ${i + 1}` }));

  const updateExpKey = (keyIndex: number, field: keyof ExpansionKeyConfig, value: string) => {
    if (!isExpansionModulesOverridden) return;
    const keyToUpdate = currentPageKeys[keyIndex];
    const absoluteIndex = deviceExpansionModules.findIndex(k => k.moduleNumber === keyToUpdate.moduleNumber && k.keyNumber === keyToUpdate.keyNumber);
    if (absoluteIndex === -1) return;
    setDeviceExpansionModules(prev => { const updated = [...prev]; updated[absoluteIndex] = { ...updated[absoluteIndex], [field]: value }; return updated; });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">
              {isExpansionModulesOverridden ? t('provisioningDevices.customExpansion', 'Custom Expansion Modules') : t('provisioningDevices.templateExpansion', 'Using Template Expansion')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {isExpansionModulesOverridden ? t('provisioningDevices.customExpansionDesc', 'This device has specific expansion module settings.') : t('provisioningDevices.templateExpansionDesc', 'Inheriting expansion settings from template.')}
            </p>
          </div>
        </div>
        {isExpansionModulesOverridden ? (
          <button type="button" onClick={() => { setIsExpansionModulesOverridden(false); if (selectedTemplate?.expansionModules) setDeviceExpansionModules(selectedTemplate.expansionModules as ExpansionKeyConfig[]); }}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-black text-foreground border rounded-md shadow-sm hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" />{t('provisioningDevices.resetToTemplate', 'Reset to Template')}
          </button>
        ) : (
          <button type="button" onClick={() => setIsExpansionModulesOverridden(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700">
            <Edit className="h-4 w-4" />{t('provisioningDevices.customize', 'Customize Expansion')}
          </button>
        )}
      </div>

      <FormBox title={t('provisioningDevices.expansionConfig', 'Expansion Configuration')} icon={<Keyboard className="h-4 w-4" />}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{t('provisioningTemplates.module', 'Module')}:</span>
              <select value={selectedExpModule} onChange={(e) => { setSelectedExpModule(Number(e.target.value)); setExpPage(1); }} className="px-3 py-1.5 border border-border rounded text-sm bg-background">
                {Array.from({ length: expInfo.moduleCount }, (_, i) => (<option key={i + 1} value={i + 1}>{`${t('provisioningTemplates.module', 'Module')} ${i + 1}`}</option>))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={expPerPage} onChange={(e) => { setExpPerPage(Number(e.target.value)); setExpPage(1); }} className="px-2 py-1 border border-border rounded text-sm bg-background">
              <option value={10}>10</option><option value={20}>20</option><option value={40}>40</option>
            </select>
            <span className="text-sm text-muted-foreground">{t('common.itemsPerPage', 'entries per page')}</span>
          </div>
        </div>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-medium w-16">{t('provisioningTemplates.key', 'Key')}</th>
                <th className="px-3 py-2 text-left font-medium w-60">{t('provisioningTemplates.type', 'Type')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('common.description', 'Description')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.value', 'Value')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.extension', 'Extension')}</th>
                <th className="px-3 py-2 text-left font-medium w-40">{t('provisioningTemplates.line', 'Line')}</th>
              </tr>
            </thead>
            <tbody>
              {currentPageKeys.map((key, idx) => (
                <tr key={`${key.moduleNumber}-${key.keyNumber}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 text-muted-foreground font-medium">{key.keyNumber}</td>
                  <td className="px-3 py-1">
                    {isExpansionModulesOverridden ? (
                      <Combobox single options={expInfo.types.map(type => ({ value: type.value, label: type.label, checked: key.type === type.value }))}
                        onToggle={(val) => updateExpKey(idx, 'type', val)} placeholder={t('provisioningTemplates.selectType', 'Select type...')} className="w-full" />
                    ) : <div className="px-3 py-2 text-sm text-muted-foreground">{expInfo.types.find(t => t.value === key.type)?.label || key.type}</div>}
                  </td>
                  <td className="px-3 py-1"><input type="text" value={key.description} onChange={(e) => updateExpKey(idx, 'description', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isExpansionModulesOverridden} /></td>
                  <td className="px-3 py-1"><input type="text" value={key.value} onChange={(e) => updateExpKey(idx, 'value', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isExpansionModulesOverridden} /></td>
                  <td className="px-3 py-1"><input type="text" value={key.extension} onChange={(e) => updateExpKey(idx, 'extension', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isExpansionModulesOverridden} /></td>
                  <td className="px-3 py-1">
                    {isExpansionModulesOverridden ? (
                      <Combobox single options={lineOptions.map(opt => ({ value: opt.value, label: opt.label, checked: key.line === opt.value }))}
                        onToggle={(val) => updateExpKey(idx, 'line', val)} placeholder={t('provisioningTemplates.selectLine', 'Select line...')} className="w-full" />
                    ) : <div className="px-3 py-2 text-sm text-muted-foreground">{lineOptions.find(opt => opt.value === key.line)?.label || key.line}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">{t('common.showing', 'Showing')} {startIndex + 1} {t('common.to', 'to')} {endIndex} {t('common.of', 'of')} {totalItems} {t('common.results', 'entries')}</div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setExpPage(1)} disabled={expPage === 1} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
            <button type="button" onClick={() => setExpPage(p => Math.max(1, p - 1))} disabled={expPage === 1} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5) { if (expPage <= 3) pageNum = i + 1; else if (expPage >= totalPages - 2) pageNum = totalPages - 4 + i; else pageNum = expPage - 2 + i; }
              return (<button key={pageNum} type="button" onClick={() => setExpPage(pageNum)} className={`px-3 py-1 text-sm border border-border rounded ${expPage === pageNum ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{pageNum}</button>);
            })}
            <button type="button" onClick={() => setExpPage(p => Math.min(totalPages, p + 1))} disabled={expPage === totalPages} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
            <button type="button" onClick={() => setExpPage(totalPages)} disabled={expPage === totalPages} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
          </div>
        </div>
      </FormBox>
    </div>
  );
}
